import { useMemo } from 'react';

import styled from 'styled-components';
import type { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';

import { GetLastDeliveriesBySiteIdQuery, useGetLastDeliveriesBySiteIdQuery } from 'data/__generated__';

import { DataTable } from 'components/DataTable';

import { colors } from 'constants/colors';
import { formatDateForDisplay } from 'helpers/dateTimes';

import { StatusTag } from './StatusTag';
import { DELIVERY_STATES_LABELS } from '../constants/states';

type DeliveryRow = GetLastDeliveriesBySiteIdQuery['getLastDeliveriesBySiteId'][number];

const DELIVERIES_LIMIT = 15;

/**
 * Trois états, et non deux. `deliveredMicrostoreColumnIds` n'est écrit que depuis la mise en service
 * de la livraison dynamique (premières écritures en juin 2026) : sur une livraison antérieure le
 * tableau est VIDE, et afficher « non réassortie » y serait un mensonge. Le tableau vide est donc
 * rendu « — » (non tracé) — critère tiré de la donnée, plutôt qu'une date de bascule codée en dur.
 */
function getReassortState(
    deliveredMicrostoreColumnIds: string[],
    microstoreColumnId: string,
): 'reassorted' | 'notReassorted' | 'untracked' {
    if (!deliveredMicrostoreColumnIds.length) {
        return 'untracked';
    }
    return deliveredMicrostoreColumnIds.includes(microstoreColumnId) ? 'reassorted' : 'notReassorted';
}

/**
 * La colonne « Cette colonne » n'existe que sur une fiche colonne. `deliveredMicrostoreColumnIds` ne
 * dit pas « colonnes livrées » mais « colonnes réassorties » — il n'est écrit qu'à la validation d'un
 * transfert de stock, si bien qu'une colonne café ou frigo, servie à chaque passage, en est souvent
 * absente. Il sert donc de MARQUEUR par ligne, là où sa sémantique est exacte, et jamais de filtre :
 * scoper la section dessus la laisserait vide pour une colonne sur trois.
 */
function buildColumnReassortColumn(microstoreColumnId: string): ColumnDef<DeliveryRow, unknown> {
    return {
        id: 'columnReassorted',
        accessorFn: (row) => getReassortState(row.deliveredMicrostoreColumnIds, microstoreColumnId),
        header: 'Cette colonne',
        cell: ({ row }) => {
            const state = getReassortState(row.original.deliveredMicrostoreColumnIds, microstoreColumnId);

            if (state === 'untracked') {
                return (
                    <UnknownMark title="Cette livraison n'a enregistré aucune colonne : le rattachement n'était pas encore tracé.">
                        —
                    </UnknownMark>
                );
            }

            // Infobulle native plutôt que Radix : le `Tooltip` de la librairie pose un `<button>`
            // dans une ligne qui est déjà un lien, et son `asChild` transmet une ref à `Badge`,
            // qui n'est pas `forwardRef` (React 18).
            return state === 'reassorted' ? (
                <Badge variant="default" title="Un transfert de stock a visé cette colonne ce jour-là.">
                    Réassortie
                </Badge>
            ) : (
                <Badge
                    variant="outline"
                    title="Aucun transfert de stock n'a visé cette colonne : le livreur est passé sur le site sans la réassortir."
                >
                    Non réassortie
                </Badge>
            );
        },
    };
}

function buildColumns(microstoreColumnId?: string): ColumnDef<DeliveryRow, unknown>[] {
    return [
        {
            accessorKey: 'deliveryDate',
            header: 'Date',
            cell: ({ row }) => formatDateForDisplay(row.original.deliveryDate, { displayDay: true }),
        },
        {
            accessorKey: 'state',
            header: 'État',
            cell: ({ row }) => <StatusTag state={row.original.state} />,
            sortingFn: (rowA, rowB) =>
                (DELIVERY_STATES_LABELS[rowA.original.state] || '').localeCompare(
                    DELIVERY_STATES_LABELS[rowB.original.state] || '',
                ),
        },
        ...(microstoreColumnId ? [buildColumnReassortColumn(microstoreColumnId)] : []),
        {
            id: 'roundName',
            accessorFn: (row) => row.roundName ?? '',
            header: 'Tournée',
            cell: ({ row }) => {
                const { roundName, roundColor } = row.original;
                if (!roundName) return '-';
                return (
                    <span className="inline-flex items-center gap-1.5">
                        <span
                            className="inline-block size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: roundColor || colors.pantinGrey }}
                        />
                        {roundName}
                    </span>
                );
            },
        },
        {
            id: 'deliveryPerson',
            accessorFn: (row) => row.deliveryPerson?.profile.fullname ?? '-',
            header: 'Livreur',
        },
    ];
}

/**
 * L'historique des livraisons d'un SITE : une livraison est le passage d'un livreur sur un site.
 * Partagé par la fiche site et la fiche colonne — cette dernière passe `microstoreColumnId` pour
 * marquer, par ligne, si la colonne a été réassortie ce jour-là.
 */
export const SiteDeliveriesSection = ({
    siteId,
    microstoreColumnId,
    dataTest = 'site-deliveries-section',
}: {
    siteId: string;
    microstoreColumnId?: string;
    dataTest?: string;
}) => {
    const {
        data,
        loading,
        error: deliveriesError,
    } = useGetLastDeliveriesBySiteIdQuery({
        variables: { siteId, limit: DELIVERIES_LIMIT },
        fetchPolicy: 'cache-and-network',
    });

    const columns = useMemo(() => buildColumns(microstoreColumnId), [microstoreColumnId]);

    // Erreur en ligne : l'historique ne doit pas emporter la fiche avec lui.
    if (deliveriesError) {
        return <ErrorMessage>Une erreur est survenue lors de la récupération des livraisons du site</ErrorMessage>;
    }

    return (
        // La recherche de la DataTable ne fait pas `preventDefault` sur Entrée
        // (`components/DataTable/DataTableToolbar.tsx`) et cette section vit DANS le formulaire de la
        // fiche : sans cette garde, Entrée enregistrerait la fiche et naviguerait vers la première
        // ligne. Le correctif de fond appartient à la DataTable, il est consigné dans
        // `tasks/microstore-column-page-sections.md`.
        <TableContainer
            data-test={dataTest}
            onKeyDownCapture={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }}
        >
            <DataTable<DeliveryRow>
                columns={columns}
                data={data?.getLastDeliveriesBySiteId ?? []}
                filterMode="client"
                isLoading={loading}
                getRowId={(row) => row._id}
                getRowLink={(row) => `/delivery/${row._id}`}
                defaultSortBy="deliveryDate"
                defaultSortOrder="desc"
                searchPlaceholder="Tournée, livreur…"
                emptyMessage="Aucune livraison enregistrée sur ce site."
                disableUrlSync
                autoFocusSearch={false}
            />
        </TableContainer>
    );
};

// Hauteur bornée : la DataTable remplit son parent.
const TableContainer = styled.div`
    display: flex;
    height: 400px;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
`;

const UnknownMark = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;
